import type { RTCSession } from 'jssip/lib/RTCSession';

import { SessionStatus } from 'features/sip/enums';
import type { RtcConnection } from 'features/sip/interfaces';

export const isRtcConnectionActive = (
  rtcConnection: RtcConnection
): boolean => {
  // Only 'closed' is definitively inactive. 'disconnected' is recoverable via ICE restart
  if (rtcConnection.connectionState === 'closed') {
    console.log('DEBUG: RTC connection is in closed state.');
    return false;
  }

  return true;
};

export const isRtcSessionTerminated = (
  rtcSession: RTCSession | null
): boolean => {
  return !rtcSession || rtcSession.status === SessionStatus.STATUS_TERMINATED;
};

/**
 * Check if a session is completely disconnected by examining its connection state.
 * Returns true only when the session is definitely shut down.
 * 'disconnected' is treated as temporary and can recover.
 *
 * @param session - The RTCSession to check
 * @returns true if the session appears completely disconnected, false if it appears active
 */
export const isSessionCompletelyDisconnected = (
  session: RTCSession | null
): boolean => {
  if (!session) {
    return true;
  }

  // Session ended in JsSIP
  if (session.isEnded?.()) {
    return true;
  }

  const pc = session.connection;
  if (!pc) {
    // No RTCPeerConnection means no active WebRTC media/data flow
    return true;
  }

  // Shortcut variables for readability
  const { connectionState, iceConnectionState, signalingState } = pc;

  // Anything that means "fully closed or failed"
  const isClosed =
    connectionState === 'closed' ||
    connectionState === 'failed' ||
    signalingState === 'closed' ||
    iceConnectionState === 'closed' ||
    iceConnectionState === 'failed';

  if (isClosed) {
    return true;
  }

  // 'disconnected' can recover, so don't treat it as dead yet
  return false;
};

export const getAudioSender = (
  rtcConnection: RtcConnection
): RTCRtpSender | undefined => {
  const sender =
    typeof rtcConnection.getSenders === 'function'
      ? rtcConnection
          .getSenders()
          .find((candidate) => candidate.track?.kind === 'audio')
      : undefined;

  if (sender) {
    return sender;
  }

  // Fallback: sender.track may be null, such as after replaceTrack(null).
  // The transceiver still identifies the negotiated media m-line.
  if (typeof rtcConnection.getTransceivers !== 'function') {
    return undefined;
  }

  return rtcConnection
    .getTransceivers()
    .find(
      (transceiver) =>
        transceiver.sender &&
        (transceiver.sender.track?.kind === 'audio' ||
          transceiver.receiver?.track?.kind === 'audio')
    )?.sender;
};
